import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PreviewData } from '../../interfaces/api.interface';
import { SupplierData } from '../../interfaces/supplier-data.interface';
import { TagReplacement, DictContentStructure } from '../../interfaces/ai-content.interface';
import { WebsiteGenerationService } from '../website-generation.service';
import { WebsiteFileService } from '../website-file.service';
import { IndexedDBService } from '../indexeddb.service';
import { FormBuilderV2Service } from './form-builder-v2.service';
import { TemplateVariableService } from '../template-variable.service';
import { PhoneFormatterUtil } from '../../utils/phone-formatter.util';

/**
 * Service to manage blur states for template elements
 * Provides persistent, state-based blur management
 */
class BlurStateService {
  private blurStates: Map<string, boolean> = new Map();
  private indexedDBService: IndexedDBService;
  private companyGuid: string = '';

  constructor(indexedDBService: IndexedDBService) {
    this.indexedDBService = indexedDBService;
  }

  /**
   * Set the company GUID for IndexedDB operations
   */
  setCompanyGuid(guid: string): void {
    this.companyGuid = guid;
  }

  /**
   * Mark an element as permanently unblurred (disabled)
   */
  markAsUnblurred(tagName: string, reason: 'ai' | 'user' | 'manual' | 'fallback'): void {
    // Blur functionality disabled
  }

  /**
   * Mark an element as blurred (placeholder state) (disabled)
   */
  markAsBlurred(tagName: string): void {
    // Blur functionality disabled
  }

  /**
   * Check if an element should be blurred (disabled)
   */
  shouldBeBlurred(tagName: string): boolean {
    // Blur functionality disabled - always return false (no blur)
    return false;
  }

  /**
   * Get all blur states as an object (disabled)
   */
  getBlurStates(): { [tagName: string]: boolean } {
    // Blur functionality disabled - return empty object
    return {};
  }

  /**
   * Get all blur states as an object with metadata (disabled)
   */
  getBlurStatesWithMetadata(): { [tagName: string]: { shouldBeBlurred: boolean; reason?: string } } {
    // Blur functionality disabled - return empty object
    return {};
  }

  /**
   * Clear all blur states (disabled)
   */
  clearAllStates(): void {
    // Blur functionality disabled
  }

  /**
   * Persist blur states to IndexedDB (disabled)
   */
  private async persistToIndexedDB(): Promise<void> {
    // Blur functionality disabled - no persistence
  }

  /**
   * Restore blur states from IndexedDB (disabled)
   */
  async restoreFromIndexedDB(): Promise<void> {
    // Blur functionality disabled - no restoration
  }

  /**
   * Initialize blur states for all tagged elements in HTML (disabled)
   */
  initializeFromHTML(html: string): void {
    // Blur functionality disabled
  }
}

@Injectable({
  providedIn: 'root'
})
export class PreviewV2Service {
  private previewUpdateSubject = new BehaviorSubject<PreviewData | null>(null);
  public previewUpdate$: Observable<PreviewData | null> = this.previewUpdateSubject.asObservable();

  // Edit mode state
  private isEditModeSubject = new BehaviorSubject<boolean>(false);
  public isEditMode$: Observable<boolean> = this.isEditModeSubject.asObservable();

  private currentTemplateHtml: string = '';
  private originalTemplateHtml: string = '';
  private selectedTemplate: number = 1;
  private selectedTheme: string = 'default';
  private customThemeColor: string = '#2e58d6';
  
  // Store applied TagReplacements for semantic content extraction
  private appliedTagReplacements: TagReplacement[] = [];

  // Blur state management
  private blurStateService: BlurStateService;

  // Global flag to disable blur functionality
  private blurEnabled: boolean = false; // Disable blur functionality

  // Company GUID for IndexedDB operations
  private companyGuid: string = '';

  // Navigation restrictions for contextual browsing
  private allowedPages: string[] = ['index', 'about', 'capabilities', 'contact', 'quote', '404'];

  constructor(
    private websiteGenerationService: WebsiteGenerationService,
    private websiteFileService: WebsiteFileService,
    private indexedDBService: IndexedDBService,
    private formBuilderV2Service: FormBuilderV2Service,
    private templateVariableService: TemplateVariableService
  ) {
    this.blurStateService = new BlurStateService(indexedDBService);
  }

  /**
   * Set allowed pages for navigation restrictions
   */
  setAllowedPages(allowedPages: string[]): void {
    const previousPages = [...this.allowedPages];
    this.allowedPages = allowedPages;
    console.log('PreviewV2Service: Navigation restricted to pages:', allowedPages);
  }

  /**
   * Get currently allowed pages for navigation
   */
  getAllowedPages(): string[] {
    return this.allowedPages;
  }

  /**
   * Check if a page is allowed for navigation
   */
  isPageAllowed(pageName: string): boolean {
    return this.allowedPages.includes(pageName);
  }

  /**
   * Apply navigation restrictions to HTML content
   */
  private applyNavigationRestrictions(html: string): string {
    // Replace navigation links with JavaScript calls and apply navigation restrictions
    const pageMappings = [
      { pattern: /href="about\.html"/g, pageName: 'about' },
      { pattern: /href="capabilities\.html"/g, pageName: 'capabilities' },
      { pattern: /href="contact\.html"/g, pageName: 'contact' },
      { pattern: /href="quote\.html"/g, pageName: 'quote' },
      { pattern: /href="index\.html"/g, pageName: 'index' },
      { pattern: /href="404\.html"/g, pageName: '404' }
    ];

    let restrictionsApplied = 0;
    pageMappings.forEach(({ pattern, pageName }) => {
      if (this.isPageAllowed(pageName)) {
        // Allow navigation to this page
        html = html.replace(pattern, `href="javascript:parent.loadTemplatePage('${pageName}')"`);
      } else {
        // Disable navigation to this page - replace with disabled link
        html = html.replace(pattern, `href="javascript:void(0)" onclick="return false;" style="pointer-events: none; opacity: 0.5;"`);
        restrictionsApplied++;
      }
    });

    return html;
  }

  /**
   * Set the company GUID for IndexedDB operations
   */
  setCompanyGuid(guid: string): void {
    this.companyGuid = guid;
    this.blurStateService.setCompanyGuid(guid);
  }

  /**
   * Get the current company GUID
   */
  getCompanyGuid(): string {
    return this.companyGuid;
  }

  /**
   * Check if AI content has been generated (making the site editable)
   */
  hasAIContentBeenGenerated(): boolean {
    return this.formBuilderV2Service.hasAIContentBeenGenerated();
  }

  /**
   * Toggle edit mode on/off
   */
  async toggleEditMode(companyGuid?: string): Promise<void> {
    const currentMode = this.isEditModeSubject.value;
    const newMode = !currentMode;
    this.isEditModeSubject.next(newMode);
    
    // Persist edit mode state
    if (companyGuid) {
      try {
        await this.indexedDBService.storeEditMode(companyGuid, newMode);
      } catch (error) {
        console.warn('Failed to persist edit mode state:', error);
      }
    }
    
    // Blur functionality disabled - no state broadcasting
  }

  /**
   * Set edit mode explicitly
   */
  async setEditMode(enabled: boolean, companyGuid?: string): Promise<void> {
    this.isEditModeSubject.next(enabled);
    
    // Persist edit mode state
    if (companyGuid) {
      try {
        await this.indexedDBService.storeEditMode(companyGuid, enabled);
      } catch (error) {
        console.warn('Failed to persist edit mode state:', error);
      }
    }
    
    // Blur functionality disabled - no state broadcasting
  }

  /**
   * Get current edit mode state
   */
  getEditMode(): boolean {
    return this.isEditModeSubject.value;
  }

  async initializePreview(templateNumber: number, preserveTagReplacements: boolean = false): Promise<void> {
    // Clear previous template data
    this.currentTemplateHtml = '';
    this.originalTemplateHtml = '';
    
    // Only clear applied tag replacements if we're not preserving them (e.g., during restoration)
    if (!preserveTagReplacements) {
      this.appliedTagReplacements = [];
    }
    
    // Clear blur states for new template
    // this.blurStateService.clearAllStates();
    
    this.selectedTemplate = templateNumber;
    
    // Force reload when switching templates to ensure clean state
    const forceReload = true;
    await this.websiteFileService.initialize(templateNumber, forceReload);
    
    const indexFile = this.websiteFileService.getFileContent('index.html');
    this.currentTemplateHtml = indexFile ? indexFile.content as string : '';

            // CRITICAL FIX: Restore both dict.js content and tag replacements from IndexedDB on page reload
        if (this.companyGuid) {
          await this.restoreDictContentFromIndexedDB(this.companyGuid);
          await this.restoreTagReplacementsFromIndexedDB(this.companyGuid);
        }
  }

  async updatePreview(supplierData: SupplierData, template?: number, theme?: string, companyGuid?: string, isEditMode?: string | null): Promise<void> {
    if (template && template !== this.selectedTemplate) {
      this.selectedTemplate = template;
      await this.initializePreview(template);
      await this.updatePreview(supplierData, undefined, theme, companyGuid, isEditMode);
      return;
    }
    
    if (theme) {
      this.selectedTheme = theme;
    }

    // CRITICAL FIX: Ensure tag replacements are applied during file processing
    await this.websiteFileService.processFiles(supplierData, this.selectedTheme, this.appliedTagReplacements, isEditMode);
    
    const indexFile = this.websiteFileService.getFileContent('index.html');
    this.currentTemplateHtml = indexFile ? indexFile.content as string : '';

    // CRITICAL FIX: Apply tag replacements to the current template HTML after processing
    if (this.appliedTagReplacements.length > 0) {
      let updatedHtml = this.currentTemplateHtml;
      
      for (const replacement of this.appliedTagReplacements) {
        if(replacement.tagName === 'COMPANY_LOGO'){
          replacement.replacementHTML = this.websiteFileService.getBlobUrl('images/logo-placeholder.png') || '';
        }
        if (replacement.tagName.startsWith('IMG_')) {
          // Handle image replacements (including COMPANY_LOGO)
            // Handle other image replacements
            const beforeReplace = updatedHtml;
            if(replacement.tagName === 'IMG_COMPANY_LOGO'){
              const imgTagPattern = new RegExp(
                `<img([^>]*?)tag=["']${replacement.tagName}["']([^>]*?)src=["'][^"']*["']([^>]*?)>`,
                'gi'
              );
              updatedHtml = updatedHtml.replace(imgTagPattern, (match, beforeTag, betweenTagAndSrc, afterSrc) => {
                console.log(`🔄 Updated image with explicit tag ${replacement.tagName} in preview HTML`);
                return `<img${beforeTag}tag="${replacement.tagName}"${betweenTagAndSrc}src="${replacement.replacementHTML}"${afterSrc}>`;
              });
            }else{
              const imgPattern = new RegExp(
                `(<img[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*src=")[^"]*("[^>]*)>`,
                'gi'
              );
              updatedHtml = updatedHtml.replace(imgPattern, (match, beforeSrc, afterSrc) => {
                return `${beforeSrc}${replacement.replacementHTML}${afterSrc}`;
              });
          }
          
            
            
           
            
            // If no tagged images were found/replaced, look for untagged images
            if (updatedHtml === beforeReplace) {
              // Try to find and tag untagged images that match this replacement
              const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
              updatedHtml = updatedHtml.replace(imgPattern, (match, imgSrc) => {
                // Extract alt attribute if present
                const altMatch = match.match(/alt="([^"]*)"/);
                const alt = altMatch ? altMatch[1] : '';
                
                // Generate tag name for this image
                let generatedTag = '';
                if (alt && alt.trim()) {
                  generatedTag = 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
                } else if (imgSrc) {
                  const filename = imgSrc.split('/').pop()?.split('.')[0];
                  if (filename) {
                    generatedTag = 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
                  }
                }
                
                if (generatedTag === replacement.tagName) {
                  return match.replace(/src="[^"]*"/, `src="${replacement.replacementHTML}"`).replace(/<img/, `<img tag="${replacement.tagName}"`);
                }
                
                return match;
              });
            }
        } else {
          // Handle text content replacements
          const tagPattern = new RegExp(
            `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
            'gi'
          );
          
          updatedHtml = updatedHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
            return `${openTag}${replacement.replacementHTML}${closeTag}`;
          });
        }
      }
      
      this.currentTemplateHtml = updatedHtml;
    }

    // Apply navigation restrictions to the updated HTML
    const restrictedHtml = this.applyNavigationRestrictions(this.currentTemplateHtml);
    
    const previewData: PreviewData = {
      templateHtml: restrictedHtml,
      supplierData,
      template: this.selectedTemplate,
      theme: this.selectedTheme,
      customThemeColor: this.customThemeColor 
    };

    this.previewUpdateSubject.next(previewData);
  }

  private replaceImageTag(html: string, replacement: TagReplacement): string {
    const imgTagPattern = new RegExp(
      `<img([^>]*?)tag=["']${replacement.tagName}["']([^>]*?)src=["'][^"']*["']([^>]*?)>`,
      'gi'
    );
    let newHtml = html.replace(imgTagPattern, (match, beforeTag, betweenTagAndSrc, afterSrc) => {
      return `<img${beforeTag}tag="${replacement.tagName}"${betweenTagAndSrc}src="${replacement.replacementHTML}"${afterSrc}>`;
    });
    return newHtml;
  }

  async loadTemplatePage(pageName: string, supplierData: SupplierData, companyGuid?: string): Promise<void> {
    try {
      const pageFile = this.websiteFileService.getFileContent(`${pageName}.html`);
      
      if (pageFile) {
        // Initialize blur states from the page-specific HTML to ensure all tags are properly initialized
        // this.blurStateService.initializeFromHTML(pageFile.content as string);
        
        // Get the page content and apply any existing tag replacements
        let pageHtml = pageFile.content as string;
        
        // Apply existing tag replacements to the page HTML (for image replacements and content)
        if (this.appliedTagReplacements.length > 0) {
          for (const replacement of this.appliedTagReplacements) {
            // Handle both text content and image replacements
            if (replacement.tagName.startsWith('IMG_')) {
              // Image replacement - update src attribute
              pageHtml = this.replaceImageTag(pageHtml, replacement);
            } else {
              // Text content replacement
              // if(replacement.tagName !== 'COMPANY_LOGO'){
                const tagPattern = new RegExp(
                  `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
                  'gi'
                );
                pageHtml = pageHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
                  return `${openTag}${replacement.replacementHTML}${closeTag}`;
                });
            // }
            }
          }
        }
        
        // Apply navigation restrictions to the page HTML
        const restrictedPageHtml = this.applyNavigationRestrictions(pageHtml);
        
        const previewData: PreviewData = {
          templateHtml: restrictedPageHtml,
          supplierData,
          template: this.selectedTemplate,
          theme: this.selectedTheme,
          customThemeColor:  this.customThemeColor 
        };
        
        this.previewUpdateSubject.next(previewData);
      } else {
        console.warn(`Template page not found: ${pageName}`);
      }
    } catch (error) {
      console.error(`Error loading template page ${pageName}:`, error);
    }
  }

  generatePreviewHtml(data: PreviewData): string {
    let html = data.templateHtml || '';

    // Replace template variables first
    html = this.templateVariableService.replaceTemplateVariables(html);
    
    // CRITICAL FIX: Apply tag replacements to HTML before processing blob URLs
    if (this.appliedTagReplacements.length > 0) {
      for (const replacement of this.appliedTagReplacements) {
        if (replacement.tagName.startsWith('IMG_')) {
            html = this.replaceImageTag(html, replacement);
        }
      }
    }

    // Apply supplier data placeholders (company name, address, etc.)
    html = html.replace(/\{\{COMPANY_NAME\}\}/g, data.supplierData?.companyName || 'Your Company');
    html = html.replace(/\{\{COMPANY_ADDRESS\}\}/g, data.supplierData?.streetAddress || '123 Business St');
    html = html.replace(/\{\{COMPANY_PHONE\}\}/g, PhoneFormatterUtil.formatPhoneNumber(data.supplierData?.phoneNumber || '(555) 123-4567'));
    html = html.replace(/\{\{COMPANY_EMAIL\}\}/g, data.supplierData?.contactEmail || 'contact@yourcompany.com');

    // Apply theme
    if ( data.customThemeColor) {
      // Enhanced custom/default theme color application
      console.log('PreviewV2Service: Applying theme color:', data.customThemeColor, 'for theme:', data.theme);
      
      // Replace CSS variables and default colors
      html = html.replace(/var\(--primary-color\)/g, data.customThemeColor);
      html = html.replace(/#2e58d6/g, data.customThemeColor);
      
      // Add comprehensive CSS overrides for custom/default theme
      const customThemeCSS = `
        <style>
          :root {
            --primary-color: ${data.customThemeColor} !important;
          }
          .btn-primary, .btn, button[type="submit"], input[type="submit"] {
            background-color: ${data.customThemeColor} !important;
            border-color: ${data.customThemeColor} !important;
          }
          .btn-primary:hover, .btn:hover, button[type="submit"]:hover, input[type="submit"]:hover {
            background-color: ${this.darkenColor(data.customThemeColor)} !important;
            border-color: ${this.darkenColor(data.customThemeColor)} !important;
          }
          .text-primary, .text-theme {
            color: ${data.customThemeColor} !important;
          }
          .border-primary, .border-theme {
            border-color: ${data.customThemeColor} !important;
          }
          .bg-primary, .bg-theme {
            background-color: ${data.customThemeColor} !important;
          }
        </style>
      `;
      
      // Insert custom theme CSS in the head
      if (html.includes('</head>')) {
        html = html.replace('</head>', `${customThemeCSS}</head>`);
      } else {
        html = customThemeCSS + html;
      }
    }

    // Add edit functionality if edit mode is enabled
    if (this.getEditMode()) {
      html = this.addHoverDetectionToTaggedElements(html);
    }

    // Replace relative paths with blob URLs for assets
    html = html.replace(/href="css\/([^"]+)"/g, (match, filename) => {
      const cssPath = `css/${filename}`;
      const cssUrl = this.websiteFileService.getBlobUrl(cssPath);
      return cssUrl ? `href="${cssUrl}"` : match;
    });

    html = html.replace(/src="js\/([^"]+)"/g, (match, filename) => {
      const jsPath = `js/${filename}`;
      const jsUrl = this.websiteFileService.getBlobUrl(jsPath);
      return jsUrl ? `src="${jsUrl}"` : match;
    });

    // CRITICAL FIX: Handle image src replacement more carefully
    // Only replace images that don't already have data URLs (to preserve replaced images)
    html = html.replace(/src="images\/([^"]*)"(?![^>]*data:)/g, (match, imagePath) => {
      // Skip if this is already a data URL (replaced image)
      const fullMatch = match.substring(0, 200); // Check context
      if (fullMatch.includes('data:')) {
        return match;
      }
      
      const fullImagePath = `images/${imagePath}`;
      const imageUrl = this.websiteFileService.getBlobUrl(fullImagePath);
      
      if (imageUrl) {
        return `src="${imageUrl}"`;
      }
      
      return match;
    });

    // Apply navigation restrictions
    html = this.applyNavigationRestrictions(html);
    return html;
  }

  setTemplate(templateNumber: number): void {
    this.selectedTemplate = templateNumber;
  }

  setTheme(theme: string): void {
    this.selectedTheme = theme;
  }

  setCustomThemeColor(color: string): void {
    // Store custom theme color for use in preview
    this.customThemeColor = color;
    
    // If theme is currently 'custom' or 'default', trigger immediate preview update
    if (this.selectedTheme === 'custom' || this.selectedTheme === 'default') {
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          customThemeColor: color
        };
        this.previewUpdateSubject.next(updatedPreview);
      }
    }
  }

  getCustomThemeColor(): string {
    return this.customThemeColor;
  }

  private darkenColor(color: string): string {
    // Simple color darkening function
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Darken by 20%
    const factor = 0.8;
    const newR = Math.round(r * factor);
    const newG = Math.round(g * factor);
    const newB = Math.round(b * factor);
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  getCurrentTemplate(): number {
    return this.selectedTemplate;
  }

  getCurrentTheme(): string {
    return this.selectedTheme;
  }

  async getDeploymentZip(): Promise<File> {
    const blob = await this.websiteFileService.packageForDeployment();
    return new File([blob], 'website.zip', { type: 'application/zip' });
  }

  /**
   * Synchronize current preview state to deployment files
   * Ensures WYSIWYG - what you see in preview is exactly what gets deployed
   */
  async syncPreviewToDeploymentFiles(): Promise<void> {
    console.log('🔄 PreviewV2Service: Starting WYSIWYG sync for deployment...');
    
    if (this.appliedTagReplacements.length === 0) {
      console.log('✅ PreviewV2Service: No tag replacements to sync');
      return;
    }
    
    console.log(`🔄 PreviewV2Service: Syncing ${this.appliedTagReplacements.length} tag replacements to memory files`);
    
    // Get all HTML files that need tag replacement updates
    const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html', '404.html'];
    
    for (const fileName of htmlFiles) {
      const fileContent = this.websiteFileService.getFileContent(fileName);
      if (fileContent && typeof fileContent.content === 'string') {
        let updatedHtml = fileContent.content as string;
        
        // Apply all current tag replacements to this file
        for (const replacement of this.appliedTagReplacements) {
          // EXACT SAME LOGIC AS PREVIEW SYSTEM (loadTemplatePage method)
          // Handle both text content and image replacements
          if (replacement.tagName.startsWith('IMG_')) {
            // Image replacement - update src attribute (same as preview)
            console.log(`🔄 PreviewV2Service: Applying image tag replacement for ${replacement.tagName} in ${fileName}`);
            updatedHtml = this.replaceImageTag(updatedHtml, replacement);
          } else {
            // Text content replacement (same as preview)
            console.log(`🔄 PreviewV2Service: Applying text tag replacement for ${replacement.tagName} in ${fileName}`);
            const tagPattern = new RegExp(
              `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
              'gi'
            );
            updatedHtml = updatedHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
              return `${openTag}${replacement.replacementHTML}${closeTag}`;
            });
          }
        }
        
        // Update the file content in memory
        this.websiteFileService.updateFileContent(fileName, updatedHtml);
        console.log(`✅ PreviewV2Service: Updated ${fileName} with current tag replacements`);
      }
    }
    
    console.log('✅ PreviewV2Service: WYSIWYG sync completed - deployment files now match preview');
  }

  getFileCount(): number {
    return this.websiteFileService.getFileCount();
  }

  /**
   * Remove blur effects from all placeholder elements for deployment
   * This method modifies the HTML to remove blur effects before deployment
   */
  removeBlurEffectsForDeployment(): void {
    
    // Update all HTML files to remove blur effects
    const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html', '404.html'];
    
    htmlFiles.forEach(fileName => {
      const fileContent = this.websiteFileService.getFileContent(fileName);
      if (fileContent && typeof fileContent.content === 'string') {
        let html = fileContent.content as string;
        
        // Remove blur-section class from all elements
        html = html.replace(/class="([^"]*)\s*blur-section\s*([^"]*)"/g, (match, before, after) => {
          const classes = `${before} ${after}`.trim().replace(/\s+/g, ' ');
          return classes ? `class="${classes}"` : '';
        });
        
        // Also remove standalone blur-section classes
        html = html.replace(/class="blur-section"/g, '');
        
        // Update the file content
        this.websiteFileService.updateFileContent(fileName, html);
      }
    });
    
    // Update the current template HTML
    if (this.currentTemplateHtml) {
      this.currentTemplateHtml = this.currentTemplateHtml.replace(/class="([^"]*)\s*blur-section\s*([^"]*)"/g, (match, before, after) => {
        const classes = `${before} ${after}`.trim().replace(/\s+/g, ' ');
        return classes ? `class="${classes}"` : '';
      });
      this.currentTemplateHtml = this.currentTemplateHtml.replace(/class="blur-section"/g, '');
    }
  }

  async getTemplateHtml(): Promise<string> {
    return this.currentTemplateHtml;
  }

  async getOriginalTagContent(tagName: string): Promise<string> {
    const htmlToSearch = this.originalTemplateHtml || this.currentTemplateHtml;
    
    const tagPattern = new RegExp(`<([^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*)>([^<]*)</[^>]*>`, 'i');
    const match = htmlToSearch.match(tagPattern);
    
    if (match) {
      return match[2] || '';
    }

    const selfClosingPattern = new RegExp(`<[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>`, 'i');
    const selfClosingMatch = htmlToSearch.match(selfClosingPattern);
    
    if (selfClosingMatch) {
      return 'Original content';
    }

    return 'Original content';
  }

  async applyAIContent(tagReplacements: TagReplacement[], companyGuid?: string): Promise<void> {
    try {
      if (!this.originalTemplateHtml) {
        this.originalTemplateHtml = this.currentTemplateHtml;
      }

      // Merge the new tag replacements with existing ones instead of replacing
      // This preserves existing replacements like COMPANY_LOGO
      for (const replacement of tagReplacements) {
        if(replacement.tagName==='IMG_COMPANY_LOGO'){
          continue;
        }
        const existingIndex = this.appliedTagReplacements.findIndex(r => r.tagName === replacement.tagName);
        if (existingIndex >= 0) {
          this.appliedTagReplacements[existingIndex] = replacement;
        } else {
          this.appliedTagReplacements.push(replacement);
        }
      }

      // Set AI content generated flag
      await this.formBuilderV2Service.setAIContentGenerated(true);

      // Enable edit mode when AI content is first applied
      if (!this.getEditMode()) {
        this.setEditMode(true);
      }

      let updatedHtml = this.currentTemplateHtml;

      // Define company tags that should preserve user-provided content
      const companyTags = ['COMPANY_NAME', 'COMPANY_ADDRESS', 'COMPANY_PHONE', 'COMPANY_EMAIL'];

      for (const replacement of tagReplacements) {
        // Skip company tags to preserve user-provided content
        if (companyTags.includes(replacement.tagName)) {
          continue;
        }
        if(replacement.tagName.startsWith('IMG_')){
          continue;
        }
        
        const tagPattern = new RegExp(
          `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
          'gi'
        );

        updatedHtml = updatedHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
          return `${openTag}${replacement.replacementHTML}${closeTag}`;
        });
      }

      this.currentTemplateHtml = updatedHtml;

      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html', '404.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          // CRITICAL FIX: Apply AI content replacements to the CURRENT (placeholder-replaced) content
          // instead of starting from original template content
          let fileHtml = fileContent.content;
          
          for (const replacement of tagReplacements) {
            // Skip company tags to preserve user-provided content
            if (companyTags.includes(replacement.tagName)) {
              continue;
            }
            if(replacement.tagName.startsWith('IMG_')){
              continue;
            }
            
            const tagPattern = new RegExp(
              `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)(.*?)(</[^>]*>)`,
              'gi'
            );

            fileHtml = fileHtml.replace(tagPattern, (match, openTag, content, closeTag) => {
              return `${openTag}${replacement.replacementHTML}${closeTag}`;
            });
          }
          
          // Update file content while preserving placeholder replacements
          this.websiteFileService.updateFileContent(fileName, fileHtml);
        }
      }

      // Apply navigation restrictions to the updated HTML
      const restrictedHtml = this.applyNavigationRestrictions(updatedHtml);
      
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: restrictedHtml
        };
        this.previewUpdateSubject.next(updatedPreview);
      }

      // PERSIST AI CONTENT TO INDEXEDDB
      if (companyGuid) {
        try {
          await this.persistAIContentToIndexedDB(companyGuid, tagReplacements);
        } catch (error) {
          console.warn('Failed to persist AI content to IndexedDB:', error);
        }
      }

    } catch (error) {
      throw error;
    }
  }

  /**
   * Add hover detection and edit functionality to tagged elements in HTML
   */
  private addHoverDetectionToTaggedElements(html: string): string {
    // Add CSS for hover effects (no edit icon)
    const hoverCSS = `
      <style>
        .editable-element {
          position: relative;
          transition: box-shadow 0.2s cubic-bezier(0.4,0,0.2,1);
          cursor: pointer;
        }
        
        .editable-element:hover,
        .editable-element.editing-active {
          /* Remove outline and border, add heavy boxy shadow */
          outline: none;
          border: none;
          border-radius: 6px;
          box-shadow: 0 8px 32px 0 rgba(46, 88, 214, 0.32), 0 2px 12px 0 rgba(0,0,0,0.18), 0 0 0 4px rgba(46, 88, 214, 0.10);
        }
        
        .floating-edit-tooltip {
          display: none;
          position: fixed;
          background: #2e58d6;
          color: #fff;
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(46, 88, 214, 0.18);
          z-index: 99999;
          pointer-events: none;
          opacity: 0.97;
          left: 0;
          top: 0;
          transition: opacity 0.15s;
        }
      </style>
    `;

    // Add JavaScript for edit functionality (no edit icon)
    const editScript = `
      <script>
        function initializeEditFunctionality() {
          
          // Create a single floating tooltip
          let tooltip = document.getElementById('floating-edit-tooltip');
          if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.id = 'floating-edit-tooltip';
            tooltip.className = 'floating-edit-tooltip';
            tooltip.innerText = 'Click to edit content';
            document.body.appendChild(tooltip);
          }

          let lastActive = null;
          
          // Function to generate a unique tag name for untagged images
          function generateImageTagName(imgElement) {
            const alt = imgElement.alt || '';
            const src = imgElement.src || '';
            
            // Try to extract meaningful name from alt text
            if (alt && alt.trim()) {
              return 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
            }
            
            // For blob URLs, extract the blob ID (this is the key fix)
            if (src && src.startsWith('blob:')) {
              const blobId = src.split('/').pop();
              if (blobId) {
                return 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
              }
            }
            
            // Try to extract from src filename
            if (src) {
              const filename = src.split('/').pop().split('.')[0];
              if (filename) {
                return 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
              }
            }
            
            // Fallback to consistent hash-based naming (same logic as TypeScript)
            function hashString(str) {
              let hash = 0;
              for (let i = 0; i < str.length; i++) {
                const char = str.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash; // Convert to 32-bit integer
              }
              // Convert to positive integer and then to base36 string
              return Math.abs(hash).toString(36).substr(0, 8);
            }
            
            const srcHash = hashString(src || 'default');
            return 'IMG_' + srcHash.toUpperCase();
          }
          
          // Get all tagged elements
          const taggedElements = document.querySelectorAll('[tag]:not(img)');
          
          // Get all img elements that don't have tags
          // const untaggedImages = document.querySelectorAll('img:not([tag])');
          
          // Combine both sets of elements
          const editableElements = [...taggedElements];
          

          
          
          
          editableElements.forEach(element => {
            if(element.tagName.toLowerCase() === 'img'){
              return;
            }
            element.classList.add('editable-element');
            
            // Generate tag for untagged images
            if (element.tagName.toLowerCase() === 'img' && !element.hasAttribute('tag')) {
              const generatedTag = generateImageTagName(element);
              element.setAttribute('tag', generatedTag);
            }

            element.addEventListener('mouseenter', function(e) {
              const rect = element.getBoundingClientRect();
              tooltip.style.display = 'block';
              tooltip.style.left = (rect.left + rect.width/2 - tooltip.offsetWidth/2) + 'px';
              tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
              tooltip.style.opacity = '0.97';
              
              // Update tooltip text based on element type
              if (element.tagName.toLowerCase() === 'img') {
                tooltip.innerText = 'Click to change image';
              } else {
                tooltip.innerText = 'Click to edit content';
              }
            });
            element.addEventListener('mousemove', function(e) {
              const rect = element.getBoundingClientRect();
              tooltip.style.left = (rect.left + rect.width/2 - tooltip.offsetWidth/2) + 'px';
              tooltip.style.top = (rect.top - tooltip.offsetHeight - 8) + 'px';
            });
            element.addEventListener('mouseleave', function(e) {
              tooltip.style.display = 'none';
            });
            element.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              
              // Remove .editing-active from any previously active element
              if (lastActive) lastActive.classList.remove('editing-active');
              element.classList.add('editing-active');
              lastActive = element;
              const tagName = element.getAttribute('tag');
              const rect = element.getBoundingClientRect();
              
              // Check if this is an image element
              if (element.tagName.toLowerCase() === 'img') {
                const currentImageSrc = element.src || '';
                // Always use postMessage for iframe communication
                window.parent.postMessage({
                  type: 'openImageUploader',
                  tagName: tagName,
                  currentImageSrc: currentImageSrc,
                  elementRect: {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height
                  }
                }, '*');
              } else {
                // Handle text content editing
                const textContent = element.textContent || '';
                const innerText = element.innerText || '';
                const currentContent = textContent || innerText || '';
                // Always use postMessage for iframe communication
                window.parent.postMessage({
                  type: 'openContentEditor',
                  tagName: tagName,
                  currentContent: currentContent,
                  elementRect: {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height
                  }
                }, '*');
              }
            });
          });

          // Listen for a message from parent to clear editing highlight
          window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'closeInlineEditor') {
              if (lastActive) lastActive.classList.remove('editing-active');
              lastActive = null;
            }
          });
        }
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initializeEditFunctionality);
        } else {
          initializeEditFunctionality();
        }
        window.addEventListener('load', function() {
          setTimeout(initializeEditFunctionality, 100);
        });
      </script>
    `;

    // Insert CSS and JavaScript into the HTML
    let modifiedHtml = html;
    
    // Add CSS to head
    if (modifiedHtml.includes('</head>')) {
      modifiedHtml = modifiedHtml.replace('</head>', `${hoverCSS}</head>`);
    } else {
      modifiedHtml = hoverCSS + modifiedHtml;
    }
    
    // Add JavaScript before closing body tag
    if (modifiedHtml.includes('</body>')) {
      modifiedHtml = modifiedHtml.replace('</body>', `${editScript}</body>`);
    } else {
      modifiedHtml = modifiedHtml + editScript;
    }
    
    return modifiedHtml;
  }

  /**
   * Remove blur from a specific tag element (legacy method for backward compatibility)
   */
//   removeBlurFromTag(tagName: string): void {
//     console.log('🎯 Legacy removeBlurFromTag called for tag:', tagName);
//     // Use the new state-based system
//     this.updateBlurState(tagName, false, 'manual');
//   }

  /**
   * Update content in the current preview for a specific tag
   */
  async updateContentInPreview(tagName: string, newContent: string, companyGuid?: string): Promise<void> {
    try {
      // ✅ ARCHITECTURAL FIX: Handle content vs image updates differently
      if (tagName.startsWith('IMG_')) {
        return; // Let updateImageInPreview handle this
      }

      
      // Update the dict.js file and IndexedDB
      await this.updateSingleTagInDict(tagName, newContent);
      
      // Refresh the preview to reload the updated dict.js content
      await this.refreshPreviewIframe();

    } catch (error) {
      console.error(`Failed to update content for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Update image in the current preview for a specific tag
   */
  async updateImageInPreview(tagName: string, newImageSrc: string, companyGuid?: string, fileName?: string): Promise<void> {
    try {

      const existingIndex = this.appliedTagReplacements.findIndex(r => r.tagName === tagName);
      if (existingIndex >= 0) {
        this.appliedTagReplacements[existingIndex].replacementHTML = newImageSrc;
        if (fileName) {
          this.appliedTagReplacements[existingIndex].fileName = fileName;
        }
        console.log(`🔄 Updated existing tag replacement for ${tagName}${fileName ? ` with filename: ${fileName}` : ''}`);
      } else {
        this.appliedTagReplacements.push({
          tagName,
          replacementHTML: newImageSrc,
          ...(fileName && { fileName })
        });
        console.log(`➕ Added new tag replacement for ${tagName}${fileName ? ` with filename: ${fileName}` : ''}`);
      }
      
       const currentPreview = this.previewUpdateSubject.value;
      
          if(currentPreview){
          const restrictedHtml = this.applyNavigationRestrictions(currentPreview?.templateHtml || '');
          const updatedPreview: PreviewData = {
            ...currentPreview,
            templateHtml: restrictedHtml
          };          
          this.previewUpdateSubject.next(updatedPreview);
        }
  

      // Save state after image update
      if (companyGuid) {
        try {
          const updatedReplacement = {
            tagName: tagName,
            replacementHTML: newImageSrc,
            ...(fileName && { fileName })
          };
          
          // Merge the single update with existing replacements
          await this.indexedDBService.mergeTagReplacements(companyGuid, [updatedReplacement], 'newer');
          
        } catch (error) {
          console.warn('Failed to save state after image update:', error);
        }
      }

    } catch (error) {
      console.error(`Failed to update image for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Replace an image blob in the file service and update indexDB
   */
  async replaceImageBlob(tagName: string, imageBlob: Blob, companyGuid?: string): Promise<string | null> {
    try {
      // Find the image file path based on the tag name
      const imagePath = this.findImagePathByTag(tagName);
      
      if (imagePath) {
        // Update the image file in the website file service with the new blob
        this.websiteFileService.updateFileWithBlob(imagePath, imageBlob);
        
        // Regenerate blob URLs to ensure the updated content is properly mapped
        this.websiteFileService.regenerateBlobUrls();
        
        // Sync the updated files to IndexedDB (using existing mechanism)
        await this.websiteFileService.syncToIndexedDB();
        
        // Get the blob URL for the updated content
        const blobUrl = this.websiteFileService.getBlobUrl(imagePath);
        
        // Apply navigation restrictions to the updated HTML
        const restrictedHtml = this.applyNavigationRestrictions(this.currentTemplateHtml);
        
        // Trigger an immediate preview refresh so the updated content is displayed
        const currentPreview = this.previewUpdateSubject.value;
        if (currentPreview) {
          console.log(`🔄 Triggering immediate preview refresh for updated image blob: ${tagName}`);
          this.previewUpdateSubject.next({
            ...currentPreview,
            templateHtml: restrictedHtml
          });
        }
        

        
        return blobUrl;
      } else {
        console.warn(`Could not find image path for tag "${tagName}"`);
        return null;
      }
    } catch (error) {
      console.error(`❌ Failed to replace image blob for tag "${tagName}":`, error);
      throw error;
    }
  }

  /**
   * Generate a consistent hash from a string for tag naming
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Convert to positive integer and then to base36 string
    return Math.abs(hash).toString(36).substr(0, 8);
  }

  /**
   * Find the image file path based on the tag name
   */
  private findImagePathByTag(tagName: string): string | null {
    // Look for the image in the current template HTML
    const htmlToSearch = this.currentTemplateHtml || this.originalTemplateHtml;
    
    // First, try to find images with explicit tags
    const taggedImgPattern = new RegExp(
      `<img[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*src="([^"]*)"[^>]*>`,
      'gi'
    );
    
    const taggedMatch = taggedImgPattern.exec(htmlToSearch);
    if (taggedMatch) {
      return taggedMatch[1];
    }
    
    // If no tagged images found, look for untagged images that might match the generated tag
    const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
    let imgMatch;
    
    while ((imgMatch = imgPattern.exec(htmlToSearch)) !== null) {
      const fullImgTag = imgMatch[0];
      const imgSrc = imgMatch[1];
      
      // Extract alt attribute if present
      const altMatch = fullImgTag.match(/alt="([^"]*)"/);
      const alt = altMatch ? altMatch[1] : '';
      
      // Generate tag name for this image (same logic as in JavaScript)
      const generateImageTagName = (imgSrc: string, alt: string = '') => {
        if (alt && alt.trim()) {
          return 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        }
        
        // For blob URLs, extract the blob ID (this is the key fix)
        if (imgSrc && imgSrc.startsWith('blob:')) {
          const blobId = imgSrc.split('/').pop();
          if (blobId) {
            return 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        if (imgSrc) {
          const filename = imgSrc.split('/').pop()?.split('.')[0];
          if (filename) {
            return 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          }
        }
        
        // Use a consistent fallback based on image src hash for consistent tag generation
        const srcHash = this.hashString(imgSrc || 'default');
        return 'IMG_' + srcHash.toUpperCase();
      };
      
      const generatedTag = generateImageTagName(imgSrc, alt);
      
      // If this image would generate the same tag name, return its path
      if (generatedTag === tagName) {
        return imgSrc;
      }
    }
    
    return null;
  }

  /**
   * Extract current semantic content from stored TagReplacements for three-context change requests
   */
  getCurrentSemanticContent(): { [tagName: string]: string } {
    const semanticContent: { [tagName: string]: string } = {};
    
    // Convert stored TagReplacements to semantic content object
    for (const replacement of this.appliedTagReplacements) {
      semanticContent[replacement.tagName] = replacement.replacementHTML;
    }
    
    return semanticContent;
  }

  /**
   * Get only IMG tags from template HTML files based on the current active section
   * @param currentActiveSection - The current active section ('about-us', 'capabilities', 'logo', etc.)
   * @returns Object containing IMG tags and their sources for the specified section
   */
  getCurrentImageSemanticContent(currentActiveSection?: string): { [tagName: string]: string } {
    if (currentActiveSection === 'logo') {
      return this.getLogoSectionImages(); // Filtered images
    } else if (currentActiveSection === 'about-us') {
      return this.getAboutUsImages(); // All about.html images
    } else if (currentActiveSection === 'capabilities') {
      return this.getCapabilitiesImages(); // All capabilities.html images
    } else {
      // Default: get all files (backward compatibility)
      return this.getAllImagesFromFiles(['index.html', 'about.html', 'capabilities.html', 'contact.html']);
    }
  }

  /**
   * Get images specifically for the About Us section
   * @returns Object containing IMG tags and their sources from about.html
   */
  private getAboutUsImages(): { [tagName: string]: string } {
    console.log('Processing About Us section images...');
    return this.getAllImagesFromFiles(['about.html'], 'about-us');
  }

  /**
   * Get images specifically for the Capabilities section
   * @returns Object containing IMG tags and their sources from capabilities.html
   */
  private getCapabilitiesImages(): { [tagName: string]: string } {
    console.log('Processing Capabilities section images...');
    return this.getAllImagesFromFiles(['capabilities.html'], 'capabilities');
  }

  /**
   * Get images specifically for the Logo section (filtered to exclude duplicates)
   * @returns Object containing IMG tags and their sources from index.html + contact.html, excluding duplicates
   */
  private getLogoSectionImages(): { [tagName: string]: string } {
    console.log('Processing Logo section images with duplicate filtering...');
    
    // Get all images from index.html and contact.html (no section filtering for logo section)
    const allLogoImages = this.getAllImagesFromFiles(['index.html', 'contact.html']);
    
    // Get images from other sections to exclude (with section filtering to exclude logos)
    const aboutUsImages = this.getAllImagesFromFiles(['about.html'], 'about-us');
    const capabilitiesImages = this.getAllImagesFromFiles(['capabilities.html'], 'capabilities');
    
    // Filter out duplicates based on src comparison
    const filteredImages: { [tagName: string]: string } = {};
    
    for (const [tagName, imgSrc] of Object.entries(allLogoImages)) {
      if (!this.isImageInOtherSections(imgSrc, aboutUsImages, capabilitiesImages)) {
        filteredImages[tagName] = imgSrc;
      } else {
        console.log(`Filtering out duplicate image ${tagName} (${imgSrc}) from logo section`);
      }
    }
    
    console.log(`Logo section filtered images:`, Object.keys(filteredImages));
    return filteredImages;
  }

  /**
   * Extract images from specified HTML files
   * @param htmlFiles - Array of HTML file names to process
   * @param currentActiveSection - The current active section for logo filtering
   * @returns Object containing IMG tags and their sources
   */
  private getAllImagesFromFiles(htmlFiles: string[], currentActiveSection?: string): { [tagName: string]: string } {
    const imageSemanticContent: { [tagName: string]: string } = {};
    
    console.log(`Processing IMG tags from files: ${htmlFiles.join(', ')}`);
    
    for (const fileName of htmlFiles) {
      const fileContent = this.websiteFileService.getFileContent(fileName);
      if (fileContent && typeof fileContent.content === 'string') {
        const htmlContent = fileContent.content;
        
        // Extract IMG tags from this HTML file
        const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
        let imgMatch;
        
        while ((imgMatch = imgPattern.exec(htmlContent)) !== null) {
          const fullImgTag = imgMatch[0];
          const imgSrc = imgMatch[1];
          
          // Check if this img has a tag attribute
          const tagMatch = fullImgTag.match(/tag="([^"]+)"/);
          let tagName: string;
          
          if (tagMatch) {
            // Use existing tag
            tagName = tagMatch[1];
          } else {
            // Generate a tag name for untagged images
            const altMatch = fullImgTag.match(/alt="([^"]*)"/);
            const alt = altMatch ? altMatch[1] : '';
            const filename = imgSrc.split('/').pop()?.split('.')[0] || '';
            
            if (alt && alt.trim()) {
              tagName = 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
            } else if (filename) {
              tagName = 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
            } else {
              // Use consistent hash-based fallback
              const srcHash = this.hashString(imgSrc || 'default');
              tagName = 'IMG_' + srcHash.toUpperCase();
            }
          }
          
          // Skip logo images for about-us and capabilities sections
          if ((currentActiveSection === 'about-us' || currentActiveSection === 'capabilities') && 
              (tagName.includes('LOGO') || tagName.includes('logo'))) {
            console.log(`Skipping logo image ${tagName} for section ${currentActiveSection}`);
            continue;
          }
          
          // Check if we have a stored replacement for this tag (user-uploaded images)
          const storedReplacement = this.appliedTagReplacements.find(r => r.tagName === tagName);
          
          if (storedReplacement) {
            // Use the stored AI-generated content (user-uploaded images)
            imageSemanticContent[tagName] = storedReplacement.replacementHTML;
          } else {
            // Use the current image src from template (default template images)
            imageSemanticContent[tagName] = imgSrc;
          }
        }
      }
    }
    
    console.log(`Extracted IMG semantic content from files ${htmlFiles.join(', ')}:`, Object.keys(imageSemanticContent));
    return imageSemanticContent;
  }

  /**
   * Check if an image exists in other sections (duplicate detection)
   * @param imgSrc - The image source to check
   * @param aboutUsImages - Images from about-us section
   * @param capabilitiesImages - Images from capabilities section
   * @returns True if the image is found in other sections
   */
  private isImageInOtherSections(imgSrc: string, aboutUsImages: { [tagName: string]: string }, capabilitiesImages: { [tagName: string]: string }): boolean {
    // Check if image exists in about-us section
    const isInAboutUs = Object.values(aboutUsImages).some(src => this.areImagesSimilar(imgSrc, src));
    
    // Check if image exists in capabilities section
    const isInCapabilities = Object.values(capabilitiesImages).some(src => this.areImagesSimilar(imgSrc, src));
    
    return isInAboutUs || isInCapabilities;
  }

  /**
   * Check if two images are similar (duplicate detection logic)
   * @param src1 - First image source
   * @param src2 - Second image source
   * @returns True if images are considered similar/duplicate
   */
  private areImagesSimilar(src1: string, src2: string): boolean {
    // Exact match
    if (src1 === src2) {
      return true;
    }
    
    // Compare filename (without path)
    const filename1 = src1.split('/').pop()?.toLowerCase();
    const filename2 = src2.split('/').pop()?.toLowerCase();
    
    if (filename1 && filename2 && filename1 === filename2) {
      return true;
    }
    
    // Compare filename without extension
    const name1 = filename1?.split('.')[0];
    const name2 = filename2?.split('.')[0];
    
    if (name1 && name2 && name1 === name2) {
      return true;
    }
    
    return false;
  }

  /**
   * Get only IMG tag names from the current template HTML based on section
   * @param currentActiveSection - The current active section ('about-us', 'capabilities', 'logo', etc.)
   * @returns An array of IMG tag names for easy iteration
   */
  getCurrentImageTagNames(currentActiveSection?: string): string[] {
    const imageSemanticContent = this.getCurrentImageSemanticContent(currentActiveSection);
    console.log('Image Semantic Content:', Object.keys(imageSemanticContent));
    return Object.keys(imageSemanticContent);
  }

  /**
   * Get current image source for a specific IMG tag based on section
   * @param tagName - The IMG tag name (e.g., 'IMG_HERO', 'IMG_TEAM')
   * @param currentActiveSection - The current active section ('about-us', 'capabilities', 'logo', etc.)
   * @returns The current image source URL
   */
  getCurrentImageSource(tagName: string, currentActiveSection?: string): string {
    const imageSemanticContent = this.getCurrentImageSemanticContent(currentActiveSection);
    return imageSemanticContent[tagName] || '';
  }

  /**
   * Get stored TagReplacements for backward compatibility
   */
  getStoredTagReplacements(): TagReplacement[] {
    return [...this.appliedTagReplacements];
  }



  /**
   * Extract all semantic tags from current template HTML (including those without stored replacements)
   */
  extractAllSemanticTags(): { [tagName: string]: string } {
    const semanticContent: { [tagName: string]: string } = {};
    const htmlToSearch = this.currentTemplateHtml || this.originalTemplateHtml;
    
    // Extract all semantic tags from HTML (text content)
    const tagPattern = /<[^>]*tag="([^"]+)"[^>]*>([^<]*)<\/[^>]*>/gi;
    let match;
    
    while ((match = tagPattern.exec(htmlToSearch)) !== null) {
      const tagName = match[1];
      const currentContent = match[2];
      
      // Check if we have a stored replacement for this tag
      const storedReplacement = this.appliedTagReplacements.find(r => r.tagName === tagName);
      
      if (storedReplacement) {
        // Use the stored AI-generated content
        semanticContent[tagName] = storedReplacement.replacementHTML;
      } else {
        // Use the current content from HTML
        semanticContent[tagName] = currentContent.trim();
      }
    }
    
    // Extract all img tags (both tagged and untagged)
    const imgPattern = /<img[^>]*src="([^"]*)"[^>]*>/gi;
    let imgMatch;
    
    while ((imgMatch = imgPattern.exec(htmlToSearch)) !== null) {
      const fullImgTag = imgMatch[0];
      const imgSrc = imgMatch[1];
      
      // Check if this img has a tag attribute
      const tagMatch = fullImgTag.match(/tag="([^"]+)"/);
      let tagName: string;
      
      if (tagMatch) {
        // Use existing tag
        tagName = tagMatch[1];
      } else {
        // Generate a tag name for untagged images
        const altMatch = fullImgTag.match(/alt="([^"]*)"/);
        const alt = altMatch ? altMatch[1] : '';
        const filename = imgSrc.split('/').pop()?.split('.')[0] || '';
        
        if (alt && alt.trim()) {
          tagName = 'IMG_' + alt.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        } else if (imgSrc && imgSrc.startsWith('blob:')) {
          // For blob URLs, extract the blob ID (this is the key fix)
          const blobId = imgSrc.split('/').pop();
          if (blobId) {
            tagName = 'IMG_' + blobId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
          } else {
            const srcHash = this.hashString(imgSrc || 'default');
            tagName = 'IMG_' + srcHash.toUpperCase();
          }
        } else if (filename) {
          tagName = 'IMG_' + filename.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
        } else {
          // Use consistent hash-based fallback
          const srcHash = this.hashString(imgSrc || 'default');
          tagName = 'IMG_' + srcHash.toUpperCase();
        }
      }
      
      // Check if we have a stored replacement for this tag
      const storedReplacement = this.appliedTagReplacements.find(r => r.tagName === tagName);
      
      if (storedReplacement) {
        // Use the stored AI-generated content
        semanticContent[tagName] = storedReplacement.replacementHTML;
      } else {
        // Use the current image src
        semanticContent[tagName] = imgSrc;
      }
    }
    
    return semanticContent;
  }

  /**
   * Apply updated semantic content from change request response
   */
  async applyUpdatedSemanticContent(updatedSemanticContent: { [tagName: string]: string }): Promise<void> {
    try {
      // Convert updated semantic content back to TagReplacement format
      const updatedTagReplacements: TagReplacement[] = [];
      
      for (const [tagName, content] of Object.entries(updatedSemanticContent)) {
        updatedTagReplacements.push({
          tagName,
          replacementHTML: content
        });
      }
      
      // Apply the updated content
      await this.applyAIContent(updatedTagReplacements);
      
    } catch (error) {
      console.error('❌ Failed to apply updated semantic content:', error);
      throw error;
    }
  }

  /**
   * Clear stored TagReplacements (useful for reset operations)
   */
  clearStoredTagReplacements(): void {
    this.appliedTagReplacements = [];
  }

  async resetToOriginalContent(): Promise<void> {
    if (this.originalTemplateHtml) {
      this.currentTemplateHtml = this.originalTemplateHtml;
      
      // Clear stored tag replacements when resetting
      this.clearStoredTagReplacements();
      
      const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html', '404.html'];
      
      for (const fileName of htmlFiles) {
        const fileContent = this.websiteFileService.getFileContent(fileName);
        if (fileContent && typeof fileContent.content === 'string') {
          if (fileName === 'index.html') {
            this.websiteFileService.updateFileContent(fileName, this.originalTemplateHtml);
          }
        }
      }
      
      // Apply navigation restrictions to the original HTML
      const restrictedHtml = this.applyNavigationRestrictions(this.originalTemplateHtml);
      
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        const updatedPreview: PreviewData = {
          ...currentPreview,
          templateHtml: restrictedHtml
        };
        this.previewUpdateSubject.next(updatedPreview);
      }
    }
  }

  /**
   * Initialize preview with restored content from IndexedDB
   * This method ensures tag replacements are restored before template initialization
   */
  async initializePreviewWithRestoration(templateNumber: number, companyGuid: string, editMode?: string | null): Promise<void> {
    try {
      // First, restore tag replacements from IndexedDB
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(companyGuid);
      
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        this.appliedTagReplacements = storedTagReplacements;
        
        // Initialize preview while preserving the restored tag replacements
        await this.initializePreview(templateNumber, true);
        
        // Apply the restored content to the template
        await this.applyAIContent(storedTagReplacements);
      } else {
        // No stored content, initialize normally
        await this.initializePreview(templateNumber, false);
      }

      // Separate section: Restore image tags from IndexedDB
      await this.restoreImageTagsFromIndexedDB(companyGuid);
      
      // CRITICAL FIX: Trigger preview update to emit to preview component
      // This was missing and caused the preview component to never receive updates
      const defaultSupplierData: SupplierData = {
        companyName: 'Your Company',
        contactEmail: '',
        phoneNumber: '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        countryRegion: '',
        postalCode: '',
        aboutContent: '',
        capabilities: '',
        equipment: '',
        websiteURL: '',
        logoURL: ''
      };
      
      console.log('🎯 DEBUG: Triggering preview update after initialization');
      await this.updatePreview(defaultSupplierData, templateNumber, this.selectedTheme, companyGuid, editMode);
      
    } catch (error) {
      console.error('Failed to initialize preview with restoration:', error);
      // Fallback to normal initialization
      await this.initializePreview(templateNumber, false);
      
      // CRITICAL FIX: Also trigger preview update in fallback case
      const defaultSupplierData: SupplierData = {
        companyName: 'Your Company',
        contactEmail: '',
        phoneNumber: '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        countryRegion: '',
        postalCode: '',
        aboutContent: '',
        capabilities: '',
        equipment: '',
        websiteURL: '',
        logoURL: ''
      };
      
      console.log('🎯 DEBUG: Triggering preview update after fallback initialization');
      await this.updatePreview(defaultSupplierData, templateNumber, this.selectedTheme, companyGuid, editMode);
    }
  }

  /**
   * Persist AI content to IndexedDB for state restoration
   */
  private async persistAIContentToIndexedDB(companyGuid: string, tagReplacements: TagReplacement[]): Promise<void> {
    try {
      // CRITICAL FIX: Use merge instead of overwrite to preserve all generated tags
      // This ensures that equipment tags 1-6 and other tags are not lost when new content is generated
      await this.indexedDBService.mergeTagReplacements(companyGuid, tagReplacements, 'newer');
      
      // Store AI content state
      const aiContentState = {
        tagReplacements,
        hasChanges: true,
        appliedAt: new Date().toISOString()
      };
      await this.indexedDBService.storeAIContent(companyGuid, aiContentState);
      
      console.log('✅ AI content persisted to IndexedDB for state restoration (merged)');
    } catch (error) {
      console.error('Failed to persist AI content to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Restore AI content from IndexedDB on initialization
   */
  async restoreAIContentFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(companyGuid);
      const storedAIContent = await this.indexedDBService.getAIContent(companyGuid);
      
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        // Restore the applied tag replacements (this may have already been done in initializePreviewWithRestoration)
        if (this.appliedTagReplacements.length === 0) {
          this.appliedTagReplacements = storedTagReplacements;
          
          // Apply the AI content to the current template
          await this.applyAIContent(storedTagReplacements);
        }
      }
    } catch (error) {
      console.error('Failed to restore AI content from IndexedDB:', error);
    }
  }

  /**
   * Restore edit mode state from IndexedDB on initialization
   */
  async restoreEditModeFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedEditMode = await this.indexedDBService.getEditMode(companyGuid);
      
      if (storedEditMode !== null) {
        this.isEditModeSubject.next(storedEditMode);
      }
    } catch (error) {
      console.error('Failed to restore edit mode state from IndexedDB:', error);
    }
  }

  /**
   * Restore image tags from IndexedDB - specifically handles tags starting with 'img'
   */
  private async restoreImageTagsFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(companyGuid);
      
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        // Filter for image tags - specifically those starting with 'img'
        const imageTags = storedTagReplacements.filter(replacement => 
          replacement.tagName.toLowerCase().startsWith('img') ||
          replacement.tagName === 'COMPANY_LOGO' ||
          replacement.replacementHTML.includes('<img') ||
          replacement.replacementHTML.startsWith('data:image') ||
          replacement.replacementHTML.startsWith('blob:')
        );
        
        if (imageTags.length > 0) {
          console.log('✅ Restoring image tags from IndexedDB:', imageTags.map(tag => tag.tagName));
          
          // Ensure preview update subject has valid data before calling updateImageInPreview
          const currentPreview = this.previewUpdateSubject.value;
          if (!currentPreview) {
            console.log('ℹ️ No current preview data, initializing preview state before image restoration');
            // Set initial preview data to ensure updateImageInPreview has a valid base to work with
            const initialPreviewData: PreviewData = {
              templateHtml: this.currentTemplateHtml || '',
              supplierData: {} as SupplierData, // Will be populated when actual supplier data is available
              template: this.selectedTemplate,
              theme: this.selectedTheme,
              customThemeColor: this.selectedTheme === 'custom' ? this.customThemeColor : undefined
            };
            this.previewUpdateSubject.next(initialPreviewData);
          }
          
          for (const imageTag of imageTags) {
            if (imageTag.replacementHTML) {
              await this.updateImageInPreview(imageTag.tagName, imageTag.replacementHTML, companyGuid, imageTag.fileName);
            }
          }
        } else {
          console.log('ℹ️ No image tags found in stored tag replacements');
        }
      } else {
        console.log('ℹ️ No stored tag replacements found in IndexedDB');
      }
    } catch (error) {
      console.error('Failed to restore image tags from IndexedDB:', error);
    }
  }

  /**
   * Update dict.js file with new content
   */
  async updateDictJsFile(dictContent: DictContentStructure): Promise<void> {
    try {
      console.log('PreviewV2Service: Updating dict.js file with new content');
      
      // Generate JavaScript content for dict.js
      const dictJsContent = this.generateDictJsContent(dictContent);
      
      // Update the dict.js file in the template
      await this.updateTemplateFile('js/dict.js', dictJsContent);
      
      // Refresh preview iframe to load updated dict.js
      // await this.refreshPreviewIframe();
      
      console.log('PreviewV2Service: Successfully updated dict.js file');
    } catch (error) {
      console.error('PreviewV2Service: Failed to update dict.js file:', error);
      throw error;
    }
  }

  /**
   * Generate JavaScript content for dict.js
   */
  private generateDictJsContent(dictContent: DictContentStructure): string {
    const content = `SITE_CONTENT = ${JSON.stringify(dictContent, null, 2)};`;
    return content;
  }

  /**
   * Update template file content
   */
  private async updateTemplateFile(filePath: string, content: string): Promise<void> {
    try {
      // Update the file content in memory using WebsiteFileService
      this.websiteFileService.updateFileContent(filePath, content);
      
      // Regenerate blob URLs to ensure the updated content is available
      this.websiteFileService.regenerateBlobUrls();
      
      // Sync updated files to IndexedDB for persistence
      await this.websiteFileService.syncToIndexedDB();
      
      
      // Trigger preview refresh to show updated content
      // await this.refreshPreviewIframe();
    } catch (error) {
      console.error('PreviewV2Service: Failed to update template file:', error);
      throw error;
    }
  }

  /**
   * Refresh preview iframe to load updated dict.js
   */
  private async refreshPreviewIframe(): Promise<void> {
    try {
      // Trigger a refresh of the preview iframe
      console.log('PreviewV2Service: Triggering preview iframe refresh');
      
      // Get the current preview data and emit it to trigger a refresh
      const currentPreview = this.previewUpdateSubject.value;
      if (currentPreview) {
        // Emit the current preview data to trigger a refresh with updated dict.js content
        this.previewUpdateSubject.next(currentPreview);
        console.log('PreviewV2Service: Emitted current preview data for refresh');
      } else {
        console.warn('PreviewV2Service: No current preview data available for refresh');
      }
    } catch (error) {
      console.error('PreviewV2Service: Failed to refresh preview iframe:', error);
      throw error;
    }
  }

  /**
   * Update single tag in dict.js
   */
  async updateSingleTagInDict(tagName: string, newContent: string): Promise<void> {
    try {      
      // Get current dict content from IndexedDB
      const currentDict = await this.indexedDBService.getDictContent(this.companyGuid);
      if (!currentDict) {
        console.warn('PreviewV2Service: No dict content found for company:', this.companyGuid);
        return;
      }
      
      // Update the specific tag
      this.updateNestedProperty(currentDict, tagName, newContent);

      // Update IndexedDB
      await this.indexedDBService.storeDictContent(this.companyGuid, currentDict);
      
      // Update dict.js file
      await this.updateDictJsFile(currentDict);
      
      console.log('PreviewV2Service: Successfully updated tag in dict.js:', tagName);
    } catch (error) {
      console.error('PreviewV2Service: Failed to update single tag in dict.js:', error);
      throw error;
    }
  }

  /**
   * Update nested property in dict structure
   */
  private updateNestedProperty(obj: any, tagName: string, value: string): void {
    // First, find the existing tag in the dict structure
    const existingPath = this.findTagPath(obj, tagName);
    
    if (existingPath) {
      // Update the existing tag at its correct path
      const keys = existingPath.split('.');
      let current = obj;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      console.log(`Updated existing tag '${tagName}' at path: ${existingPath}`);
    } else {
      console.warn(`Tag '${tagName}' not found in dict structure. Cannot update.`);
    }
  }

  /**
   * Find the path of a tag in the dict structure
   */
  private findTagPath(obj: any, tagName: string, currentPath: string = ''): string | null {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = currentPath ? `${currentPath}.${key}` : key;
      
      if (key === tagName) {
        return fullPath;
      }
      
      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          // ✅ Handle arrays: check each element
          for (let i = 0; i < value.length; i++) {
            const arrayPath = `${fullPath}.${i}`;
            const foundPath = this.findTagPath(value[i], tagName, arrayPath);
            if (foundPath) {
              return foundPath;
            }
          }
        } else {
          // Handle regular objects
          const foundPath = this.findTagPath(value, tagName, fullPath);
          if (foundPath) {
            return foundPath;
          }
        }
      }
    }
    
    return null;
  }

  /**
   * Apply dict content to preview
   */
  async applyDictContent(dictContent: DictContentStructure): Promise<void> {
    try {
      
      // Store dict content in IndexedDB
      await this.indexedDBService.storeDictContent(this.companyGuid, dictContent);
      
      // Update dict.js file
      await this.updateDictJsFile(dictContent);
      
      // Convert to tag replacements for backward compatibility
      // const tagReplacements = this.convertDictToTagReplacements(dictContent);
      
      // Apply tag replacements to preview
      // await this.applyAIContent(tagReplacements, this.companyGuid);
      
      console.log('PreviewV2Service: Successfully applied dict content to preview');
    } catch (error) {
      console.error('PreviewV2Service: Failed to apply dict content:', error);
      throw error;
    }
  }

  /**
   * Get current dict.js file content for debugging
   */
  getCurrentDictJsContent(): string | null {
    try {
      const dictFile = this.websiteFileService.getFileContent('js/dict.js');
      if (dictFile && typeof dictFile.content === 'string') {
        return dictFile.content;
      }
      return null;
    } catch (error) {
      console.error('PreviewV2Service: Failed to get dict.js content:', error);
      return null;
    }
  }

  /**
   * Restore dict content from IndexedDB on page reload
   */
  async restoreDictContentFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      
      // Get stored dict content from IndexedDB
      const storedDictContent = await this.indexedDBService.getDictContent(companyGuid);
      
      if (storedDictContent) {
        
        // Update the dict.js file with stored content
        await this.updateDictJsFile(storedDictContent);
        
        // Convert to tag replacements and apply to preview
        const tagReplacements = this.convertDictToTagReplacements(storedDictContent);
        this.appliedTagReplacements = tagReplacements;
        
      } else {
      }
    } catch (error) {
      console.error('PreviewV2Service: Failed to restore dict content from IndexedDB:', error);
    }
  }

  /**
   * Restore tag replacements from IndexedDB after page reload
   */
  async restoreTagReplacementsFromIndexedDB(companyGuid: string): Promise<void> {
    try {
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(companyGuid);
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        
        // Apply each tag replacement to the HTML files
        for (const replacement of storedTagReplacements) {
          await this.updateContentInPreview(replacement.tagName, replacement.replacementHTML, companyGuid);
        }
        
        // Update the applied tag replacements array
        this.appliedTagReplacements = [...storedTagReplacements];
        
      } else {
      }
    } catch (error) {
      console.error('PreviewV2Service: Failed to restore tag replacements from IndexedDB:', error);
    }
  }

  /**
   * Convert dict structure to TagReplacement[] for backward compatibility
   */
  private convertDictToTagReplacements(dictContent: DictContentStructure): TagReplacement[] {
    const tagReplacements: TagReplacement[] = [];
    
    // Flatten the nested structure
    this.flattenDictContent(dictContent, '', tagReplacements);
    
    return tagReplacements;
  }

  /**
   * Recursively flatten dict content
   */
  private flattenDictContent(obj: any, prefix: string, result: TagReplacement[]): void {
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        this.flattenDictContent(value, fullKey, result);
      } else if (typeof value === 'string') {
        result.push({
          tagName: fullKey,
          replacementHTML: value
        });
      }
    }
  }


} 